import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  signal,
  ViewChild,
} from '@angular/core';
import { toCanvas, toDataURL } from 'qrcode';

/**
 * Renders a QR code for an arbitrary string. Uses canvas under the hood for
 * high-quality rasterisation and exposes a download helper that produces a
 * 512x512 PNG as recommended by the PRD.
 *
 * Usage:
 *   <lib-qr-code [value]="link" [size]="200"></lib-qr-code>
 */
@Component({
  selector: 'lib-qr-code',
  templateUrl: './qr-code.html',
  styleUrl: './qr-code.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QrCode {
  readonly value = input.required<string>();
  readonly size = input<number>(180);
  readonly downloadFileName = input<string>('payment-link');

  private readonly injector = inject(Injector);

  @ViewChild('canvas', { static: true })
  private canvasRef!: ElementRef<HTMLCanvasElement>;

  protected readonly hasError = signal<boolean>(false);
  protected readonly isReady = signal<boolean>(false);

  protected readonly displaySize = computed(() => `${this.size()}px`);

  constructor() {
    afterNextRender(
      () => {
        effect(
          () => {
            const value = this.value();
            const size = this.size();
            this.renderTo(value, size);
          },
          { injector: this.injector },
        );
      },
      { injector: this.injector },
    );
  }

  private renderTo(value: string, size: number): void {
    if (!this.canvasRef?.nativeElement) {
      return;
    }
    this.hasError.set(false);
    this.isReady.set(false);
    toCanvas(this.canvasRef.nativeElement, value || ' ', {
      width: size,
      margin: 1,
      color: { dark: '#0F172A', light: '#FFFFFF' },
      errorCorrectionLevel: 'M',
    })
      .then(() => this.isReady.set(true))
      .catch(() => this.hasError.set(true));
  }

  /**
   * Downloads a high-resolution (512px) PNG of the current QR. Suitable for
   * print and social-media share assets.
   */
  async download(): Promise<void> {
    try {
      const dataUrl = await toDataURL(this.value() || ' ', {
        width: 512,
        margin: 2,
        color: { dark: '#0F172A', light: '#FFFFFF' },
        errorCorrectionLevel: 'M',
      });
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `${this.downloadFileName()}.png`;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch {
      this.hasError.set(true);
    }
  }
}
